import { useMemo } from 'react';
import type {
  BookkeepingPacket,
  ConsignmentFeeCharge,
  ConsignmentFeeChargeDetail,
  CardTermsDetail,
  ConsignmentTermsDraft,
  ConsignmentAgreement,
  ConsignmentBatchAssignmentResponse,
  ConsignmentIntake,
  ConsignmentInventoryItem,
  ConsignmentListResponse,
  ConsignmentMutationRequest,
  ConsignmentSaleAllocation,
  ConsignmentSaleEvent,
  ConsignmentStatement,
  ConsignmentStatementDetail,
  ConsignmentFinanceDetail,
  Consignor,
  DynamicsCustomerSearchResponse,
} from '@tcvs/shared';
import { useApi } from '../api';

export function useConsignmentApi() {
  const api = useApi();
  return useMemo(
    () => ({
      listFeeCharges: (consignorId: string, cursor?: string) =>
        api.request<ConsignmentListResponse<ConsignmentFeeCharge>>(
          `/consignment?view=feeCharges&consignorId=${encodeURIComponent(consignorId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        ),
      getFeeCharge: (consignorId: string, feeChargeId: string) =>
        api.request<ConsignmentFeeChargeDetail>(
          `/consignment?view=feeCharge&consignorId=${encodeURIComponent(consignorId)}&feeChargeId=${encodeURIComponent(feeChargeId)}`,
        ),
      listBookkeeping: (consignorId: string, cursor?: string) =>
        api.request<ConsignmentListResponse<BookkeepingPacket>>(
          `/consignment?view=bookkeeping&consignorId=${encodeURIComponent(consignorId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        ),
      listConsignors: () => api.request<ConsignmentListResponse<Consignor>>('/consignment'),
      listAgreements: (consignorId: string) =>
        api.request<ConsignmentListResponse<ConsignmentAgreement>>(
          `/consignment?view=agreements&consignorId=${encodeURIComponent(consignorId)}`,
        ),
      listTermsDrafts: (consignorId: string, cursor?: string) =>
        api.request<ConsignmentListResponse<ConsignmentTermsDraft>>(
          `/consignment?view=terms-drafts&consignorId=${encodeURIComponent(consignorId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        ),
      getCardTerms: (consignorId: string, cardId: string) =>
        api.request<CardTermsDetail>(
          `/consignment?view=card-terms&consignorId=${encodeURIComponent(consignorId)}&cardId=${encodeURIComponent(cardId)}`,
        ),
      listIntakes: (consignorId: string) =>
        api.request<ConsignmentListResponse<ConsignmentIntake>>(
          `/consignment?view=intakes&consignorId=${encodeURIComponent(consignorId)}`,
        ),
      listInventory: (consignorId: string, cursor?: string) =>
        api.request<ConsignmentListResponse<ConsignmentInventoryItem>>(
          `/consignment?view=inventory&consignorId=${encodeURIComponent(consignorId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        ),
      listLedger: (consignorId: string, cursor?: string) =>
        api.request<ConsignmentListResponse<ConsignmentSaleAllocation>>(
          `/consignment?view=ledger&consignorId=${encodeURIComponent(consignorId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        ),
      listStatements: (consignorId: string, cursor?: string) =>
        api.request<ConsignmentListResponse<ConsignmentStatement>>(
          `/consignment?view=statements&consignorId=${encodeURIComponent(consignorId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        ),
      getStatement: (consignorId: string, statementId: string) =>
        api.request<ConsignmentStatementDetail>(
          `/consignment?view=statement&consignorId=${encodeURIComponent(consignorId)}&statementId=${encodeURIComponent(statementId)}`,
        ),
      getStatementFinance: (consignorId: string, statementId: string, cursor?: string) =>
        api.request<ConsignmentFinanceDetail>(
          `/consignment?view=statement-finance&consignorId=${encodeURIComponent(consignorId)}&statementId=${encodeURIComponent(statementId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        ),
      searchDynamicsCustomers: (query: string) =>
        api.request<DynamicsCustomerSearchResponse>(
          `/consignment?view=crm-customers&q=${encodeURIComponent(query)}`,
        ),
      listLedgerEvents: (consignorId: string, allocationId: string) =>
        api.request<ConsignmentListResponse<ConsignmentSaleEvent>>(
          `/consignment?view=ledger-events&consignorId=${encodeURIComponent(consignorId)}&allocationId=${encodeURIComponent(allocationId)}`,
        ),
      mutate: <T>(body: ConsignmentMutationRequest) =>
        api.request<T>('/consignment', { method: 'POST', body }),
      assignBatch: (intakeId: string, batchId: string) =>
        api.request<ConsignmentBatchAssignmentResponse>('/consignment', {
          method: 'POST',
          body: { operation: 'ASSIGN_BATCH', intakeId, batchId },
        }),
    }),
    [api],
  );
}
